import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  conversationsApi,
  type ConversationRow,
  type TurnRow,
} from "../api/conversations";

export default function Conversations() {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["conversations"],
    queryFn: () => conversationsApi.list({ limit: 100 }),
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Conversations</h2>
        <p className="mt-1 text-sm text-slate-500">
          Web chat conversations imported from claude.ai exports.
        </p>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      {data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Title / First Prompt</th>
                <th className="px-4 py-2 text-right">Turns</th>
                <th className="px-4 py-2">Tools</th>
                <th className="px-4 py-2">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((c) => (
                <ConvRow
                  key={c.id}
                  c={c}
                  isExpanded={expanded === c.id}
                  onToggle={() =>
                    setExpanded((prev) => (prev === c.id ? null : c.id))
                  }
                />
              ))}
              {data.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-sm text-slate-400"
                  >
                    No conversations yet. Upload an export via Admin · Imports.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConvRow({
  c,
  isExpanded,
  onToggle,
}: {
  c: ConversationRow;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className="cursor-pointer hover:bg-slate-50"
        onClick={onToggle}
      >
        <td className="px-4 py-2">
          <div className="font-medium text-slate-800">{c.user_name}</div>
          <div className="text-xs text-slate-500">{c.user_email}</div>
        </td>
        <td className="max-w-md px-4 py-2">
          <div className="font-medium text-slate-700 truncate">
            {c.title || "Untitled"}
          </div>
          {c.first_user_prompt && (
            <div className="mt-0.5 text-xs text-slate-500 truncate">
              {c.first_user_prompt}
            </div>
          )}
        </td>
        <td className="px-4 py-2 text-right text-slate-700">{c.turn_count}</td>
        <td className="px-4 py-2">
          {c.has_tool_calls ? (
            <span className="rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-700">
              {c.tool_integrations.length > 0
                ? c.tool_integrations.join(", ")
                : "tools"}
            </span>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </td>
        <td className="px-4 py-2 text-xs text-slate-600">
          {new Date(c.created_at).toLocaleDateString()}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={5} className="bg-slate-50 px-4 py-3">
            <TurnsPanel conversationId={c.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function TurnsPanel({ conversationId }: { conversationId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["turns", conversationId],
    queryFn: () => conversationsApi.getTurns(conversationId),
  });

  if (isLoading) return <div className="text-xs text-slate-500">Loading turns…</div>;
  if (!data || data.length === 0)
    return <div className="text-xs text-slate-400">No turns.</div>;

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {data.map((t) => (
        <TurnBubble key={t.idx} t={t} />
      ))}
    </div>
  );
}

function TurnBubble({ t }: { t: TurnRow }) {
  const isUser = t.role === "user";
  return (
    <div
      className={`rounded-lg px-3 py-2 text-sm ${
        isUser
          ? "ml-0 mr-12 bg-white border border-slate-200"
          : "ml-12 mr-0 bg-brand-50 border border-brand-200"
      }`}
    >
      <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
        <span className="font-medium">
          {isUser ? "User" : "Assistant"}
          {t.tool_call_count > 0 && (
            <span className="ml-1 text-violet-600">
              ({t.tool_call_count} tool call{t.tool_call_count > 1 ? "s" : ""})
            </span>
          )}
        </span>
        <span>{t.content_length.toLocaleString()} chars</span>
      </div>
      <div className="text-slate-700 whitespace-pre-wrap">
        {t.content_plain || (
          <span className="italic text-slate-400">
            [content not stored — EVENTS_STORE_PROMPTS was off]
          </span>
        )}
      </div>
    </div>
  );
}
